import React, {useContext, useEffect, useState} from "react";
import {GlobalStateContext} from "../../../global/context/GlobalStateContext";
import {LEDControllerOverviewContext} from "../context/LEDControllerOverviewContext";
import {FormattedMessage, injectIntl} from "react-intl";
import Modal from "@amzn/meridian/modal";
import PageFooterModal from "../../../commonui/PageFooterModal";
import Column from "@amzn/meridian/column";
import Text from "@amzn/meridian/text";
import { validateLedDefaultValues} from "../../../util/Validations";
import {chainWalk} from "../../../util/Util";
import Select, {SelectOption} from "@amzn/meridian/select";
import {ledControllerColorOrder} from "../../../util/enums";
import SearchableSelectModal from "../../../commonui/SearchableSelectModal";
import PropTypes from "prop-types";

const UpdateDefaultSettingsModal = (props) => {

    const {formatMessage} = props.intl;
    const {open, setOpen} = props;
    const {showWarning} = useContext(GlobalStateContext);
    const {viewModel, dispatch} = useContext(LEDControllerOverviewContext);

    // Visual controls
    const [showAlert, setShowAlert] = useState(false)

    // States
    const [ledControllerType, setLedControllerType] = useState(null)
    const [aisleType, setAisleType] = useState(null)
    const [colorOrder, setColorOrder] = useState(null);

    // Errors
    const [ledControllerTypeError, setLedControllerTypeError] = useState(false)
    const [aisleTypeError, setAisleTypeError] = useState(false)
    const [colorOrderError, setColorOrderError] = useState(false)
    const {showCustomAlert} = useContext(GlobalStateContext);

    useEffect(() => {
        getAisleTypes();
        dispatch({type: 'LOAD_LED_CONTROLLER_TYPES', data: {dispatcher: dispatch}})
    }, []);

    const defaultValues = {
        ledControllerDefaultValues: {
            ledControllerType: ledControllerType,
            aisleType: aisleType,
            colorOrder: colorOrder
        }
    }

    const onCloseModal = () => {
        setOpen(false)
        setShowAlert(false);
    }

    const generateSelectOptions = (types) => {
        return (
            types.map((type) =>
                <SelectOption key={type}
                              value={type}
                              label={type} />))
    }

    const getAisleTypes = () => {
        if (viewModel.aisleTypes.size === 0) {
            dispatch({type: 'LOAD_AISLE_TYPES', data: {dispatcher: dispatch}})
        }
    }

    const setSelectedDefaultValues = () => {
        setLedControllerType(chainWalk(() => viewModel.selectedDefaultValues.ledControllerType, ''))
        setColorOrder(chainWalk(() => viewModel.selectedDefaultValues.colorOrder, ''))
    }

    const onClickConfirm = (defaultValues) => {
        if (validateLedDefaultValues(defaultValues)) {
            showWarning(formatMessage({id: "device.editWarning"}), () => {
                setShowAlert(false)
                addData(defaultValues);
                resetInputState();
                setOpen(false);
            });
        } else {
            setShowAlert(true)
        }
    }

    const addData = (defaultValues) => {
        dispatch({type: 'SET_DEFAULT_VALUES', data: {dispatcher: dispatch, defaultValues: defaultValues,
                showCustomAlert: showCustomAlert}})
    }

    const resetDefaultValues = () => {
        setLedControllerType(null)
        setAisleType(null)
        setColorOrder(null)
    }

    const resetDefaultValueErrors = () => {
        setLedControllerTypeError(null)
        setAisleTypeError(null)
        setColorOrderError(null)
    }

    const resetInputState = () => {
        resetDefaultValues()
        resetDefaultValueErrors()
    }

    return (
        <Modal
            title={formatMessage({id: "node.update_defaults_button_modal"})}
            open={open}
            onOpen={setSelectedDefaultValues}
            onClose={onCloseModal}
            scrollContainer="viewport"
            closeLabel="Close"
            bodySpacingInset="medium"
            width="100%">
            <Column spacing={"medium"} spacingInset={"medium"}>
                <Column spacing="200">
                    <Text tag="label" type="b200" color="primary" htmlFor="led-controller-type-input">
                        <FormattedMessage id="modal.ledControllerType" defaultMessage="LED Controller Type"/>
                    </Text>
                    <Select id="led-controller-type-input"
                            value={ledControllerType}
                            onChange={(value) => setLedControllerType(value)}
                            onBlur={() => setLedControllerTypeError(!ledControllerType)}
                            placeholder={<FormattedMessage id="modal.selectLedControllerType" defaultMessage="Select LED Controller Type"/>}
                            error={ledControllerTypeError}
                            width="100%">
                        {
                            generateSelectOptions(viewModel.ledControllerTypes)
                        }
                    </Select>
                </Column>
                <Column spacing="200">
                    <Text tag="label" type="b200" color="primary" htmlFor="searchable-input">
                        <FormattedMessage id={formatMessage({id: "modal.aisletype"})} defaultMessage="Aisle Type" />
                    </Text>
                    <SearchableSelectModal preSelectedValue={chainWalk(() => viewModel.selectedDefaultValues.aisleType, '')}
                                           error={aisleTypeError} setError={setAisleTypeError}
                                           selectedValue={aisleType} setSelectedValue={setAisleType}
                                           htmlId="searchable-input"
                                           options={[...viewModel.aisleTypes] || []}
                                           placeholderValue={formatMessage({id: "modal.selectDefaultAisleType"})}
                                           placeholderDefaultValue={"Select Default Aisle Type"}
                    />
                </Column>
                <Column spacing="200">
                    <Text tag="label" type="b200" color="primary" htmlFor="color-order-input">
                        <FormattedMessage id="modal.colorOrder" defaultMessage="Color Order"/>
                    </Text>
                    <Select id="color-order-input"
                            value={colorOrder}
                            onChange={(value) => setColorOrder(value)}
                            onBlur={() => setColorOrderError(!colorOrder)}
                            placeholder={<FormattedMessage id="modal.selectColorOrder" defaultMessage="Select Color Order"/>}
                            error={colorOrderError}
                            width="100%">
                        {
                            generateSelectOptions(Object.keys(ledControllerColorOrder))
                        }
                    </Select>
                </Column>
            </Column>
            {<PageFooterModal showAlert={showAlert} onCloseModalCallBack={onCloseModal}
                          onClickConfirmCallBack={() => onClickConfirm(defaultValues)}
            />}
        </Modal>
    )

}

UpdateDefaultSettingsModal.propTypes = {
    intl: PropTypes.shape({
        formatMessage: PropTypes.func.isRequired
    }).isRequired,
    open: PropTypes.bool.isRequired,
    setOpen: PropTypes.func.isRequired
};

export default injectIntl(UpdateDefaultSettingsModal);


// WEBPACK FOOTER //
// ./src/components/devicemanagement/ledcontroller/modal/UpdateDefaultSettingsModal.js